import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { auth } from '../services/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUser, ensureUserDefaults, saveCustomerFcmToken, listenToUserOrders, listenToUserProfile } from '../services/firebase';
import { APP } from '../config/app';
import { normalizeAddress, formatAddressText, hasGeoCoords, createEmptyAddress } from '../utils/address';
import { buildAddressFromGps } from '../utils/geolocation';
import {
  loadDeliveryTarget, saveDeliveryTarget, clearDeliveryTarget,
  loadDeliverySession, saveDeliverySession, clearDeliverySession,
  consumePendingLocationPrompt,
} from '../utils/deliveryTarget';
import { getMapCenter } from '../config/maps';
import { isInServiceArea } from '../utils/serviceArea';
import { calcDeliveryFeeForCoords } from '../utils/deliveryFee';
import { usePlatform } from './PlatformContext';

const AppContext = createContext(null);
const CART_STORAGE_KEY = 'mytownly_cart';
const AWAITING_NAME_KEY = 'mytownly_awaiting_name';
const ACTIVE_ORDER_STATUSES = ['payment_pending', 'pending', 'confirmed', 'picked_up', 'ready'];

/** Cancelled/failed orders do not consume the first-order free delivery promo */
const FIRST_ORDER_PROMO_BLOCKING_STATUSES = [
  'pending', 'payment_pending', 'confirmed', 'ready', 'picked_up', 'delivered',
];

export const markAwaitingName = (uid) => {
  try { sessionStorage.setItem(AWAITING_NAME_KEY, uid); } catch { /* private mode */ }
};

export const clearAwaitingName = () => {
  try { sessionStorage.removeItem(AWAITING_NAME_KEY); } catch { /* */ }
};

const readAwaitingNameUid = () => {
  try { return sessionStorage.getItem(AWAITING_NAME_KEY); } catch { return null; }
};

const loadStoredCart = () => {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

export const AppProvider = ({ children }) => {
  const { platformConfig } = usePlatform();
  const mapCenter = platformConfig.storeLocation;
  const maxOrderTotal = platformConfig.maxOrderTotal;
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [cart, setCart] = useState(loadStoredCart);
  const [userLocation, setUserLocation] = useState(null);
  const [locationAddress, setLocationAddress] = useState('Piler, Andhra Pradesh');
  const [deliveryTarget, setDeliveryTargetState] = useState(() => loadDeliveryTarget());
  const [deliveryGpsLoading, setDeliveryGpsLoading] = useState(false);
  const [pinSheetOpen, setPinSheetOpen] = useState(false);
  const [pinSheetSeed, setPinSheetSeed] = useState(null);
  const [deliverySheetOpen, setDeliverySheetOpen] = useState(false);
  const [deliverySheetRequired, setDeliverySheetRequired] = useState(false);
  const [deliverySessionConfirmed, setDeliverySessionConfirmed] = useState(() => loadDeliverySession());
  const [outOfServiceAreaOpen, setOutOfServiceAreaOpen] = useState(false);
  const [outOfServiceAreaHint, setOutOfServiceAreaHint] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [profileReady, setProfileReady] = useState(false);

  const refreshUserProfile = useCallback(async (uid) => {
    const profile = await getUser(uid);
    if (profile) setUserProfile(profile);
    return profile;
  }, []);

  const applyDeliveryTarget = useCallback((target) => {
    if (!target?.address) return;
    const normalized = { ...target, address: normalizeAddress(target.address) };
    setDeliveryTargetState(normalized);
    saveDeliveryTarget(normalized);
    const short = normalized.address.area || formatAddressText(normalized.address).split(',')[0];
    setLocationAddress(short);
    if (hasGeoCoords(normalized.address)) {
      setUserLocation({ lat: normalized.address.lat, lng: normalized.address.lng });
    }
  }, []);

  const selectSavedDelivery = useCallback((addr) => {
    const normalized = normalizeAddress(addr);
    applyDeliveryTarget({ mode: 'saved', savedId: normalized.id, address: normalized });
  }, [applyDeliveryTarget]);

  const updateDeliveryAddressFields = useCallback((patch) => {
    setDeliveryTargetState((prev) => {
      if (!prev?.address) return prev;
      const next = {
        ...prev,
        address: normalizeAddress({ ...prev.address, ...patch }),
      };
      saveDeliveryTarget(next);
      return next;
    });
  }, []);

  const refreshGpsDelivery = useCallback(async (opts = {}) => {
    if (!user) throw Object.assign(new Error('Not logged in'), { code: 'AUTH' });
    setDeliveryGpsLoading(true);
    try {
      const address = await buildAddressFromGps({
        profile: userProfile,
        phone: user.phoneNumber,
        keepHouseNo: opts.keepHouseNo ?? '',
        keepBuilding: opts.keepBuilding ?? '',
        keepLandmark: opts.keepLandmark ?? '',
        keepArea: opts.keepArea ?? '',
      });
      applyDeliveryTarget({ mode: 'gps', address });
      return address;
    } finally {
      setDeliveryGpsLoading(false);
    }
  }, [user, userProfile, applyDeliveryTarget]);

  const openDeliveryPinSheet = useCallback((seedAddress = null) => {
    const center = mapCenter || getMapCenter();
    const base = seedAddress || deliveryTarget?.address || createEmptyAddress({
      ...center,
      lat: center.lat,
      lng: center.lng,
    });
    setPinSheetSeed(normalizeAddress(base));
    setPinSheetOpen(true);
  }, [deliveryTarget?.address, mapCenter]);

  const closeDeliveryPinSheet = useCallback(() => {
    setPinSheetOpen(false);
    setPinSheetSeed(null);
  }, []);

  const openDeliverySheet = useCallback((opts = {}) => {
    setDeliverySheetOpen(true);
    if (opts.required != null) setDeliverySheetRequired(opts.required);
  }, []);

  const closeDeliverySheet = useCallback(() => {
    setDeliverySheetOpen(false);
    setDeliverySheetRequired(false);
  }, []);

  const markDeliverySessionConfirmed = useCallback(() => {
    saveDeliverySession();
    setDeliverySessionConfirmed(true);
    setDeliverySheetRequired(false);
  }, []);

  const confirmDeliverySelection = useCallback((target) => {
    if (!target?.address) return false;
    const normalized = { ...target, address: normalizeAddress(target.address) };
    const { lat, lng } = normalized.address;
    if (!isInServiceArea(lat, lng)) {
      setOutOfServiceAreaOpen(true);
      return false;
    }
    applyDeliveryTarget(normalized);
    markDeliverySessionConfirmed();
    setOutOfServiceAreaOpen(false);
    return true;
  }, [applyDeliveryTarget, markDeliverySessionConfirmed]);

  const showOutOfServiceArea = useCallback((address = null) => {
    setOutOfServiceAreaHint(address);
    setOutOfServiceAreaOpen(true);
    setDeliverySheetOpen(false);
  }, []);

  const dismissOutOfServiceArea = useCallback(() => {
    setOutOfServiceAreaOpen(false);
    setOutOfServiceAreaHint(null);
    setDeliverySheetOpen(true);
    setDeliverySheetRequired(true);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        setUserProfile(null);
        setProfileReady(false);
        try {
          const profile = await ensureUserDefaults(firebaseUser.uid, firebaseUser.phoneNumber);
          setUserProfile(profile);
          if (profile?.name?.trim() && profile?.profileComplete !== false) {
            clearAwaitingName();
          } else {
            markAwaitingName(firebaseUser.uid);
          }
        } catch (e) {
          console.error('Profile init failed:', e);
          markAwaitingName(firebaseUser.uid);
          setUserProfile({
            id: firebaseUser.uid,
            phone: firebaseUser.phoneNumber || null,
            profileComplete: false,
          });
          toast.error('Profile setup had an issue — please enter your name to continue.');
        }
        setProfileReady(true);
        saveCustomerFcmToken(firebaseUser.uid).catch(() => {});
      } else {
        setUserProfile(null);
        setProfileReady(true);
        clearAwaitingName();
        setDeliveryTargetState(null);
        setDeliverySessionConfirmed(false);
        setDeliverySheetOpen(false);
        setOutOfServiceAreaOpen(false);
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!userProfile?.addresses?.length) return;
    if (deliveryTarget?.address) return;
    const def = userProfile.addresses.map(normalizeAddress).find((a) => a.isDefault)
      || normalizeAddress(userProfile.addresses[0]);
    if (hasGeoCoords(def)) {
      setUserLocation({ lat: def.lat, lng: def.lng });
      setLocationAddress(def.area || formatAddressText(def).split(',')[0]);
    }
  }, [userProfile?.addresses, deliveryTarget?.address]);

  useEffect(() => {
    if (!profileReady || !user) return;
    const awaitingUid = readAwaitingNameUid();
    const profileNeeds = userProfile?.profileComplete === false || !(userProfile?.name || '').trim();
    if (awaitingUid === user.uid || profileNeeds) return;

    if (consumePendingLocationPrompt()) {
      clearDeliverySession();
      clearDeliveryTarget();
      setDeliveryTargetState(null);
      setDeliverySessionConfirmed(false);
      setDeliverySheetOpen(true);
      setDeliverySheetRequired(true);
      return;
    }

    if (loadDeliverySession()) {
      setDeliverySessionConfirmed(true);
      const stored = loadDeliveryTarget();
      if (stored?.address) applyDeliveryTarget(stored);
      return;
    }

    setDeliverySheetOpen(true);
    setDeliverySheetRequired(true);
  }, [profileReady, user, userProfile?.profileComplete, userProfile?.name, applyDeliveryTarget]);

  useEffect(() => {
    if (!user?.uid || !deliverySessionConfirmed) return undefined;
    const onVis = () => {
      if (document.visibilityState !== 'visible' || deliveryTarget?.mode !== 'gps') return;
      refreshGpsDelivery({
        keepHouseNo: deliveryTarget.address?.houseNo || '',
        keepBuilding: deliveryTarget.address?.building || '',
      }).catch(() => {
        toast.error('Could not refresh location. Tap Change to update.');
      });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [user?.uid, deliverySessionConfirmed, deliveryTarget?.mode, deliveryTarget?.address?.houseNo, deliveryTarget?.address?.building, refreshGpsDelivery]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsub = listenToUserProfile(user.uid, (profile) => {
      if (profile) setUserProfile(profile);
    });
    return unsub;
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const refreshFcm = () => {
      if (document.visibilityState === 'visible') saveCustomerFcmToken(user.uid);
    };
    document.addEventListener('visibilitychange', refreshFcm);
    return () => document.removeEventListener('visibilitychange', refreshFcm);
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setUserOrders([]);
      setOrdersLoading(false);
      return;
    }
    setOrdersLoading(true);
    const unsub = listenToUserOrders(user.uid, (orders) => {
      setUserOrders(orders);
      setOrdersLoading(false);
    }, () => setOrdersLoading(false));
    return unsub;
  }, [user?.uid]);

  useEffect(() => {
    try {
      if (Object.keys(cart).length === 0) {
        localStorage.removeItem(CART_STORAGE_KEY);
      } else {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
      }
    } catch { /* storage full or private mode */ }
  }, [cart]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setCart({});
      try { localStorage.removeItem(CART_STORAGE_KEY); } catch { /* */ }
    }
  }, [user, authLoading]);

  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev[product.id];
      return {
        ...prev,
        [product.id]: { product, qty: existing ? existing.qty + 1 : 1 },
      };
    });
  };

  const removeFromCart = (productId) => {
    setCart((prev) => {
      const existing = prev[productId];
      if (!existing || existing.qty <= 1) {
        const next = { ...prev };
        delete next[productId];
        return next;
      }
      return { ...prev, [productId]: { ...existing, qty: existing.qty - 1 } };
    });
  };

  const clearCart = () => setCart({});

  const cartItems = Object.values(cart);
  const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cartItems.reduce((s, i) => s + i.product.price * i.qty, 0);
  const displayName = userProfile?.name
    || (user?.phoneNumber ? `+91 ${user.phoneNumber.replace('+91', '').slice(-10)}` : 'Guest');
  const savedAddresses = (userProfile?.addresses || []).map(normalizeAddress);
  const defaultAddress = savedAddresses.find((a) => a.isDefault) || savedAddresses[0];
  const awaitingNameUid = readAwaitingNameUid();
  const profileNeedsName = profileReady && (
    userProfile?.profileComplete === false || !(userProfile?.name || '').trim()
  );
  // Only decide "needs name" once the profile has actually loaded — otherwise an
  // existing user briefly flashes the name page while their profile is fetched.
  const needsName = Boolean(
    user && profileReady && (awaitingNameUid === user.uid || profileNeedsName)
  );
  const activeOrder = userOrders.find((o) => ACTIVE_ORDER_STATUSES.includes(o.status)) || null;
  const pendingPaymentOrder = userOrders.find(
    (o) => o.status === 'payment_pending' && o.paymentStatus !== 'expired' && o.paymentStatus !== 'failed',
  ) || null;

  const firstOrderFreeDelivery = useMemo(() => {
    if (!user) return false;
    return !userOrders.some((o) => FIRST_ORDER_PROMO_BLOCKING_STATUSES.includes(o.status));
  }, [user, userOrders]);

  const deliveryFeeBase = useMemo(() => {
    const addr = deliveryTarget?.address;
    if (!addr || !Number.isFinite(addr.lat) || !Number.isFinite(addr.lng)) return null;
    return calcDeliveryFeeForCoords(addr.lat, addr.lng);
  }, [deliveryTarget]);

  const calcDeliveryFee = useCallback(() => {
    if (deliveryFeeBase == null) return null;
    return firstOrderFreeDelivery ? 0 : deliveryFeeBase;
  }, [deliveryFeeBase, firstOrderFreeDelivery]);

  const deliveryFeePreview = calcDeliveryFee();
  const cartTotalWithFee = cartTotal + (deliveryFeePreview ?? 0);
  const exceedsMaxOrder = cartTotalWithFee > maxOrderTotal;

  return (
    <AppContext.Provider value={{
      user, setUser, userProfile, setUserProfile, refreshUserProfile, authLoading, profileReady, needsName, displayName,
      savedAddresses, defaultAddress,
      userOrders, ordersLoading, activeOrder, pendingPaymentOrder,
      cart, cartItems, cartCount, cartTotal, calcDeliveryFee, deliveryFeePreview, deliveryFeeBase,
      firstOrderFreeDelivery, exceedsMaxOrder, maxOrderTotal,
      addToCart, removeFromCart, clearCart,
      userLocation, setUserLocation,
      locationAddress, setLocationAddress,
      deliveryTarget, deliveryGpsLoading,
      applyDeliveryTarget, selectSavedDelivery, updateDeliveryAddressFields, refreshGpsDelivery,
      pinSheetOpen, pinSheetSeed, openDeliveryPinSheet, closeDeliveryPinSheet,
      deliverySheetOpen, deliverySheetRequired, deliverySessionConfirmed,
      openDeliverySheet, closeDeliverySheet, confirmDeliverySelection,
      markDeliverySessionConfirmed, showOutOfServiceArea, outOfServiceAreaHint,
      outOfServiceAreaOpen, dismissOutOfServiceArea,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
